import { emptySplitApi } from '../../../../store/emptyApi';

export type AssessmentQuestionSetTypeEnum = 1 | 2;
export type AssessmentQuestionSetScopeEnum = 1 | 2 | 3;
export type AssessmentQuestionTypeEnum = 1 | 2 | 3 | 4 | 5;

export interface AssessmentQuestion {
  id?: number;
  assessment_question_set?: number;
  pillar?: number | null;
  theme?: number | null;
  question: string;
  type: AssessmentQuestionTypeEnum;
  sort_order: number;
  config?: Record<string, unknown>;
  allow_feedback?: boolean;
  required?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AssessmentQuestionSet {
  id?: number;
  type: AssessmentQuestionSetTypeEnum;
  title: string;
  scope: AssessmentQuestionSetScopeEnum;
  organization?: number | null;
  team?: number | null;
  is_archived?: boolean;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
  questions?: AssessmentQuestion[];
}

export interface PillarLookup {
  id: number;
  name: string;
  sort_order?: number | null;
}

const PulseSurveyTags = {
  QuestionSet: 'PulseSurveyQuestionSet',
  Question: 'PulseSurveyQuestion',
} as const;

export const pulseSurveyApi = emptySplitApi
  .enhanceEndpoints({
    addTagTypes: [PulseSurveyTags.QuestionSet, PulseSurveyTags.Question],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      listQuestionSets: build.query<
        AssessmentQuestionSet[],
        {
          type?: number;
          scope?: number;
          include_archived?: boolean;
        }
      >({
        query: (params) => ({
          url: '/api/admin/v1/assessment-question-sets/',
          method: 'GET',
          params,
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map((row) => ({ type: PulseSurveyTags.QuestionSet, id: row.id })),
                { type: PulseSurveyTags.QuestionSet, id: 'LIST' },
              ]
            : [{ type: PulseSurveyTags.QuestionSet, id: 'LIST' }],
      }),
      createQuestionSet: build.mutation<AssessmentQuestionSet, { payload: AssessmentQuestionSet }>({
        query: ({ payload }) => ({
          url: '/api/admin/v1/assessment-question-sets/',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: [{ type: PulseSurveyTags.QuestionSet, id: 'LIST' }],
      }),
      retrieveQuestionSet: build.query<AssessmentQuestionSet, { id: number }>({
        query: ({ id }) => ({
          url: `/api/admin/v1/assessment-question-sets/${id}/`,
          method: 'GET',
        }),
        providesTags: (_, __, arg) => [{ type: PulseSurveyTags.QuestionSet, id: arg.id }],
      }),
      updateQuestionSet: build.mutation<AssessmentQuestionSet, { id: number; payload: AssessmentQuestionSet }>({
        query: ({ id, payload }) => ({
          url: `/api/admin/v1/assessment-question-sets/${id}/`,
          method: 'PUT',
          body: payload,
        }),
        invalidatesTags: (_, __, arg) => [
          { type: PulseSurveyTags.QuestionSet, id: arg.id },
          { type: PulseSurveyTags.QuestionSet, id: 'LIST' },
        ],
      }),
      patchQuestionSet: build.mutation<AssessmentQuestionSet, { id: number; payload: Partial<AssessmentQuestionSet> }>({
        query: ({ id, payload }) => ({
          url: `/api/admin/v1/assessment-question-sets/${id}/`,
          method: 'PATCH',
          body: payload,
        }),
        invalidatesTags: (_, __, arg) => [
          { type: PulseSurveyTags.QuestionSet, id: arg.id },
          { type: PulseSurveyTags.QuestionSet, id: 'LIST' },
        ],
      }),
      deleteQuestionSet: build.mutation<void, { id: number }>({
        query: ({ id }) => ({
          url: `/api/admin/v1/assessment-question-sets/${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: PulseSurveyTags.QuestionSet, id: 'LIST' }],
      }),

      listQuestions: build.query<AssessmentQuestion[], { assessment_question_set?: number }>({
        query: (params) => ({
          url: '/api/admin/v1/assessment-questions/',
          method: 'GET',
          params,
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map((row) => ({ type: PulseSurveyTags.Question, id: row.id })),
                { type: PulseSurveyTags.Question, id: 'LIST' },
              ]
            : [{ type: PulseSurveyTags.Question, id: 'LIST' }],
      }),

      listPillars: build.query<PillarLookup[], void>({
        query: () => ({
          url: '/api/admin/v1/lookup/pillars/',
          method: 'GET',
        }),
      }),
    }),
    overrideExisting: false,
  });

export const {
  useListQuestionSetsQuery,
  useCreateQuestionSetMutation,
  useRetrieveQuestionSetQuery,
  useUpdateQuestionSetMutation,
  usePatchQuestionSetMutation,
  useDeleteQuestionSetMutation,
  useListQuestionsQuery,
  useListPillarsQuery,
} = pulseSurveyApi;
